import React, { useState, useRef, useEffect, useMemo } from "react";
import { FluidCanvas } from "../components/FluidCanvas";
import { Link, useSearchParams } from "react-router";
import { Sparkles, Package, Box, Layers, Grid3x3, Loader2, FlaskConical, Boxes, Sticker, Tags, Shield, ShoppingBag, Printer, Film, ArrowRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { OptionCard } from "../components/options/OptionCard";
import { useInViewOnce } from "../hooks/useInViewOnce";
import { cn } from "../components/ui/utils";
import { HEADLINE_GRADIENT } from "../components/GradientSectionHeading";
import { BrandLogosMarquee } from "../components/BrandLogosMarquee";

type OptionItem = {
  title: string;
  description: string;
  image: string;
};

type OptionGroup = {
  label: string;
  items: OptionItem[];
};

type OptionsCategory = {
  id: string;
  label: string;
  items?: OptionItem[];
  /** When set (e.g. Box Types), sections are rendered as sub-headings with their own grids. */
  groups?: OptionGroup[];
};

type OptionsLibraryPayload = {
  categories: OptionsCategory[];
};

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  paperboard: Box,
  coatingType: Sparkles,
  laminationType: Film,
  finishes: Package,
  printing: Printer,
  corrugated: Layers,
  flutes: Grid3x3,
  plastic: FlaskConical,
  foam: Shield,
  boxTypes: Boxes,
  stickers: Sticker,
  labels: Tags,
  bagHandles: ShoppingBag,
};

export function PackagingOptionsPage() {
  const { ref: headlineRef, inView: headlineInView } = useInViewOnce({ threshold: 0.28, rootMargin: "0px 0px -10% 0px" });
  const [library, setLibrary] = useState<OptionsLibraryPayload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(searchParams.get("category") || "");
  const tabBtnRefs = useRef<Partial<Record<string, HTMLButtonElement | null>>>({});
  /** Scroll target: tab panel (offset for sticky nav + mobile tab strip). */
  const tabPanelAnchorRef = useRef<HTMLDivElement>(null);
  const tabScrollPrevRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/options-library.json")
      .then((res) => {
        if (!res.ok) throw new Error("Could not load options");
        return res.json() as Promise<OptionsLibraryPayload>;
      })
      .then((data) => {
        if (cancelled) return;
        setLibrary(data);
        if (data.categories.length > 0) {
          setActiveTab((prev) => {
            if (prev && data.categories.some((c) => c.id === prev)) return prev;
            return data.categories[0].id;
          });
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError("We couldn’t load the options catalog. Please try again later.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const tabs = useMemo(() => {
    if (!library?.categories.length) return [];
    return library.categories.map((cat) => ({
      id: cat.id,
      label: cat.label,
      icon: CATEGORY_ICONS[cat.id] ?? Box,
    }));
  }, [library]);

  const activeCategory = useMemo(() => {
    if (!library || !activeTab) return null;
    return library.categories.find((c) => c.id === activeTab) ?? null;
  }, [library, activeTab]);

  const activeItems = activeCategory?.items ?? [];
  const activeGroups = activeCategory?.groups;

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1279px)");
    if (!mq.matches) return;
    const el = tabBtnRefs.current[activeTab];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [activeTab]);

  /** After switching category, scroll so the new grid is in view (not mid-page from long lists). */
  useEffect(() => {
    if (!activeTab || !library) return;
    const prev = tabScrollPrevRef.current;
    if (prev === null) {
      tabScrollPrevRef.current = activeTab;
      return;
    }
    if (prev === activeTab) return;
    tabScrollPrevRef.current = activeTab;
    requestAnimationFrame(() => {
      tabPanelAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [activeTab, library]);

  /** Horizontal tab pills: size to label on small screens; xl+ uses sidebar, not this. */
  const mobileTabClass = (isActive: boolean) =>
    `inline-flex w-auto max-w-[min(100%,calc(100vw-2rem))] shrink-0 snap-center items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm leading-snug transition-colors sm:gap-3 sm:px-4 sm:py-3 sm:text-base ${isActive
      ? "border-[#131312] bg-[#131312] text-white shadow-md shadow-[#131312]/15"
      : "border-[#131312]/10 bg-[#F6F2EB] text-[#131312] hover:bg-[#efe9e0]"
    }`;

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* HERO SECTION */}
      <section className="relative py-32 bg-[#F6F2EB] overflow-hidden min-h-[768px] flex items-center border-b border-[#131312]/5">
        <FluidCanvas />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-2 bg-[#FBF7F4] text-[#131312] border border-[#131312]/10 rounded-full text-sm font-medium mb-8 shadow-[0_4px_12px_-4px_rgba(0,0,0,0.15)]">
              Option Library
            </div>
            <h1 ref={headlineRef} className="text-[48px] sm:text-[60px] md:text-[70px] lg:text-[78.1px] font-bold mb-8 leading-tight text-[#1C1C1C]">
              <span
                className={cn("gradient-text-animate inline", headlineInView && "gradient-text-animate--play")}
                style={{ backgroundImage: HEADLINE_GRADIENT }}
              >
                Packaging Options Library
              </span>
            </h1>
            <p
              className="font-bold leading-[25px] text-[#1C1C1C] mx-auto"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '18.6px',
                maxWidth: '627px',
                letterSpacing: '-0.5px'
              }}
            >
              Explore our comprehensive catalog of materials, finishes, and printing options. Each choice impacts the final look, feel, and performance of your packaging.
            </p>
          </div>
        </div>
      </section>

      <section className="relative bg-[#FBFAF7] pb-16 border-b border-[#131312]/5">
        {loadError && (
          <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
            <p className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-center text-destructive">
              {loadError}
            </p>
          </div>
        )}

        {!loadError && !library && (
          <div className="flex items-center justify-center gap-2 py-24 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" aria-hidden />
            <span>Loading options…</span>
          </div>
        )}

        {!loadError && library && tabs.length > 0 && (
          <>
            <div className="xl:hidden sticky top-20 z-30 w-full border-b border-border/80 bg-background/95 shadow-sm backdrop-blur-md supports-[backdrop-filter]:bg-background/90">
              <div
                className="flex w-full max-w-[100vw] snap-x snap-mandatory gap-3 overflow-x-auto overflow-y-hidden px-4 py-4 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                role="tablist"
                aria-label="Option categories"
              >
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      ref={(el) => {
                        tabBtnRefs.current[tab.id] = el;
                      }}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={mobileTabClass(activeTab === tab.id)}
                    >
                      <Icon className="size-4 shrink-0 opacity-90 sm:size-5" />
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mx-auto max-w-7xl px-6 pt-8 lg:px-8 xl:pt-16">
              <div className="flex flex-col gap-8 xl:flex-row">
                <div className="hidden w-64 shrink-0 xl:block">
                  <div className="sticky top-24 space-y-2">
                    {tabs.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          type="button"
                          id={`options-tab-${tab.id}`}
                          onClick={() => setActiveTab(tab.id)}
                          className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-colors ${activeTab === tab.id
                            ? "bg-[#131312] text-white shadow-lg shadow-[#131312]/20"
                            : "bg-[#F6F2EB] text-[#131312] hover:bg-[#efe9e0] border border-[#131312]/5"
                            }`}
                        >
                          <Icon className="size-5 flex-shrink-0" />
                          <span className="font-medium">{tab.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div
                  ref={tabPanelAnchorRef}
                  className="min-w-0 flex-1 xl:min-w-[unset] scroll-mt-[calc(5rem+5.5rem)] xl:scroll-mt-28"
                >
                  <div role="tabpanel" aria-labelledby={`options-tab-${activeTab}`}>
                    {activeGroups?.length ? (
                      <div className="space-y-14">
                        {activeGroups.map((group, gi) => (
                          <section
                            key={`${activeTab}-group-${gi}`}
                            aria-labelledby={`${activeTab}-sub-${gi}`}
                          >
                            <header className="mb-6 max-w-3xl border-b border-border/60 pb-4">
                              <h3 id={`${activeTab}-sub-${gi}`} className="text-xl font-bold">
                                {group.label}
                              </h3>
                            </header>
                            <div className={activeTab === 'paperboard' ? "grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 xl:grid-cols-2" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6"}>
                              {group.items.map((option, index) => (
                                <OptionCard
                                  key={`${group.label}-${option.title}-${index}`}
                                  option={option}
                                />
                              ))}
                            </div>
                          </section>
                        ))}
                      </div>
                    ) : (
                      <div className={activeTab === 'paperboard' ? "grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 xl:grid-cols-2" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6"}>
                        {activeItems.map((option, index) => (
                          <OptionCard key={`${option.title}-${index}`} option={option} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </section>

      {/* CTA SECTION */}
      <section className="relative py-24 bg-[#131312] text-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(72.35% 197.74% at 70% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%),
              radial-gradient(72.35% 197.74% at 30% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%)
            `
          }}
        />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white max-w-4xl mx-auto leading-[1.1]">
            Ready to Create Premium <br /> Packaging?
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Get an instant quote and see how we can elevate your brand with custom packaging solutions
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/quote"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-white text-sm font-medium text-[#131312] transition-colors hover:bg-white/90 shadow-lg sm:h-14 sm:w-auto sm:px-8 sm:text-base"
            >
              Get Instant Quote
              <ArrowRight className="ml-2 size-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-white/20 bg-white/10 px-8 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:h-14 sm:w-auto sm:text-base"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
